import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Filters from './Filters';
import { IFiltersState } from './types';
import FilterSummaryButton from './styled/FilterSummaryButton';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { filtersStateSelector } from 'store/state/selectors/search';
import { AppMode, appModeSelector, marketplaceSelector, routeSelector } from 'store/state/selectors/router';
import * as actions from 'store/state/router/actions';
import { addressDocEntrySelector } from 'store/state/domainData/selectors';
import { isEqual, noop } from 'lodash';
import { FILTERS_BUTTON_ITEM, getActiveFiltersNumber, setFiltersState } from './utils';
import { MarketplaceType } from 'utils/entities';
import withEvents from 'analytics/withEvents';
import { useLocale } from 'locale';
import { isHeaderTabSubmenuVisibleSelector } from 'store/state/app/selectors';
import { setMobileDiscoveryDisabled, setMobileDiscoveryEnabled } from 'store/state/app/actions';
import { NavigateToFn, Route, State as RouteState } from 'config/routes';
import config from 'config';
import { useScreenBreakpoint } from 'consts/breakpoints';

interface FilterWrapperProps {
  screen: 'mobile' | 'desktop';
  marketplace: MarketplaceType;
  appMode: AppMode;
  setFilters: (value: IFiltersState) => void;
  filtersState: IFiltersState;
  onFilterClick?: (label: string, type: string) => void;
  isHeaderTabSubmenuVisible: boolean;
  onlyIcon?: boolean;
  makeMobileDiscoveryDisabled: () => void;
  makeMobileDiscoveryEnabled: () => void;
  isInvestors?: boolean;
  renderCustomMobileButton?: (onClick: (e?: React.MouseEvent<HTMLElement>) => void, activeFiltersNumber: number) => React.ReactNode;
  navigate: NavigateToFn;
  route: RouteState;
}

const FilterWrapper: React.FunctionComponent<FilterWrapperProps> = (props) => {
  const {
    setFilters,
    appMode,
    filtersState,
    screen,
    onlyIcon,
    marketplace,
    onFilterClick,
    isHeaderTabSubmenuVisible,
    makeMobileDiscoveryDisabled,
    makeMobileDiscoveryEnabled,
    isInvestors,
    renderCustomMobileButton,
    navigate,
    route,
  } = props;
  const { t } = useLocale();
  const [ mobileFiltersIsActive, setMobileFiltersIsActive ] = useState(false);
  const isCombinedFiltersSearchView = useScreenBreakpoint() < 3;
  const shouldDisableFilters = appMode === AppMode.Check;

  useEffect(() => {
    if (isCombinedFiltersSearchView && route.params.mobileFiltersPopupActive) {
      makeMobileDiscoveryDisabled();
      setMobileFiltersIsActive(true);
    }
  }, []);

  useEffect(() => {
    if (!route.params.mobileFiltersPopupActive) setMobileFiltersIsActive(false);
  }, [ route ]);

  const hanldeClose = useCallback(() => {
    makeMobileDiscoveryEnabled();
    setMobileFiltersIsActive(false);
  }, [ navigate, route, makeMobileDiscoveryEnabled ]);

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    makeMobileDiscoveryDisabled();
    setMobileFiltersIsActive(true);
    onFilterClick(t('filters.filters'), 'all');

    if (isCombinedFiltersSearchView) {
      const isFiltersButtonClicked = (e.target as HTMLElement).id === FILTERS_BUTTON_ITEM;
      const isLocationEmpty = isEqual(route.params.term, [ config.cityTerm ]);
      const shouldOpenLocationSearch = isFiltersButtonClicked ? false : isLocationEmpty;
      navigate(route.name, { ...route.params, mobileFiltersPopupActive: shouldOpenLocationSearch ? 'location' : 'filters' });
    }
  }, [ navigate, route, makeMobileDiscoveryDisabled, onFilterClick, t ]);

  const setValues = (value: IFiltersState) => {
    setFilters(value);
  };

  const activeFiltersNumber = useMemo(() => getActiveFiltersNumber(filtersState, marketplace), [ filtersState, marketplace ]);

  if (screen === 'mobile') {
    return (
      <>
        {mobileFiltersIsActive ? (
          <Filters
            values={filtersState}
            setValues={setValues}
            closeFiltersModal={hanldeClose}
            disabled={shouldDisableFilters}
            marketplace={marketplace}
            isHeaderTabSubmenuVisible={isHeaderTabSubmenuVisible}
            isInvestors={isInvestors}
            combinedFiltersSearchMode={isCombinedFiltersSearchView}
          />
        ) : (
          <>
            {renderCustomMobileButton
              ? renderCustomMobileButton(handleOpen, activeFiltersNumber)
              : (
                <div>
                  <FilterSummaryButton
                    onlyIcon={onlyIcon}
                    filtersNumber={activeFiltersNumber}
                    onClick={handleOpen}
                  />
                </div>
              )}
          </>
        )}
      </>
    );
  }

  return (
    <Filters
      values={filtersState}
      setValues={setValues}
      closeFiltersModal={noop}
      disabled={shouldDisableFilters}
      marketplace={marketplace || MarketplaceType.Residential}
      isHeaderTabSubmenuVisible={isHeaderTabSubmenuVisible}
      isInvestors={isInvestors}
    />
  );
};

const FilterWrapperWithEvents = withEvents<FilterWrapperProps>((sendEvent, props) => ({
  onFilterClick(label, type) {
    sendEvent('search_filter_button_click', 'search', {
      event: {
        button_clicked: label,
        filter_category: type,
      },
    });
  },
}))(FilterWrapper);

const mapStateToProps = (state: State) => ({
  resolvedAddress: addressDocEntrySelector(state),
  marketplace: marketplaceSelector(state),
  route: routeSelector(state),
  appMode: appModeSelector(state),
  filtersState: filtersStateSelector(state),
  isHeaderTabSubmenuVisible: isHeaderTabSubmenuVisibleSelector(state),
});

const mapDispatchToProps = {
  navigate: actions.navigateTo,
  makeMobileDiscoveryDisabled: setMobileDiscoveryDisabled,
  makeMobileDiscoveryEnabled: setMobileDiscoveryEnabled,
};

export default connect(mapStateToProps, mapDispatchToProps, ({ resolvedAddress, ...stateProps }, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  isInvestors: stateProps.route.name === Route.InvestorsSearchPage,
  setFilters: (value: IFiltersState) => setFiltersState({
    ...value,
    triggerSource: 'search_filter_top_bar' },
    stateProps.route,
    resolvedAddress,
    dispatchProps.navigate
  ),
}))(FilterWrapperWithEvents);
